// Clone of SitesPage. Visual parity with the live app; "Send to Business"
// opens DemoOutreachModal and "Mark as sent" mutates only in-memory state.

import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { Globe, Edit, CheckCircle, Send } from 'lucide-react';
import { toast } from 'sonner';
import { useDemoState } from './DemoStateProvider';
import type { DemoSite } from './DemoStateProvider';
import { DemoOutreachModal } from './DemoOutreachModal';

function getStatusStyle(status: string) {
  switch (status) {
    case 'sent':
      return { background: 'rgba(91, 58, 58, 0.2)', color: '#B08080' };
    case 'sold':
      return { background: 'rgba(136, 136, 136, 0.2)', color: '#888888' };
    case 'draft':
    case 'preview':
    default:
      return { background: 'rgba(255,255,255,0.1)', color: '#888888' };
  }
}

export function DemoSites() {
  const { sites, getLead, markSiteSent } = useDemoState();
  const [outreachModalSite, setOutreachModalSite] = useState<DemoSite | null>(null);

  const handleCloseOutreachModal = (markAsSent?: boolean) => {
    if (markAsSent && outreachModalSite) {
      markSiteSent(outreachModalSite.id);
      toast.success('Site marked as sent!');
    }
    setOutreachModalSite(null);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-snow uppercase tracking-widest">Sites</h1>
        <p className="text-gray-400 mt-1">{sites.length} total sites</p>
      </div>

      {sites.length > 0 ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
          {sites.map((site) => {
            const statusStyle = getStatusStyle(site.status);

            return (
              <div
                key={site.id}
                className="hover-lift animate-list-in"
                style={{
                  background: 'rgba(15, 15, 15, 0.95)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '12px',
                  padding: '20px',
                }}
              >
                {site.businessData?.photos?.[0] ? (
                  <div style={{ aspectRatio: '16/9', borderRadius: '8px', marginBottom: '16px', overflow: 'hidden' }}>
                    <img
                      src={site.businessData.photos[0]}
                      alt={site.name}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  </div>
                ) : (
                  <div style={{
                    aspectRatio: '16/9',
                    background: 'linear-gradient(135deg, rgba(91, 58, 58, 0.1), rgba(91, 58, 58, 0.05))',
                    borderRadius: '8px',
                    marginBottom: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <Globe style={{ width: '48px', height: '48px', color: 'rgba(91, 58, 58, 0.5)' }} />
                  </div>
                )}
                <h3 style={{ fontSize: '18px', marginBottom: '4px' }}>{site.name}</h3>
                <p style={{ color: '#888888', fontSize: '13px', marginBottom: '8px' }}>{site.templateType}</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                  <span
                    style={{
                      background: statusStyle.background,
                      color: statusStyle.color,
                      padding: '4px 12px',
                      borderRadius: '9999px',
                      fontSize: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                  >
                    {site.status === 'sold' && <CheckCircle style={{ width: '12px', height: '12px' }} />}
                    {site.status}
                  </span>
                </div>

                {(site.status === 'draft' || site.status === 'preview') && (
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                    <Link
                      to={`/demo/sites/${site.id}/edit`}
                      target="_blank"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        background: 'rgba(255,255,255,0.1)',
                        color: '#FFFBFE',
                        padding: '8px 16px',
                        borderRadius: '8px',
                        fontSize: '14px',
                        textDecoration: 'none',
                      }}
                    >
                      Preview
                    </Link>
                    <Link
                      to={`/demo/sites/${site.id}/edit`}
                      style={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        background: 'linear-gradient(to right, #B08080, #B08080)',
                        color: '#FFFBFE',
                        padding: '8px 16px',
                        borderRadius: '8px',
                        textDecoration: 'none',
                        fontSize: '14px',
                      }}
                    >
                      <Edit style={{ width: '16px', height: '16px' }} />
                      Edit
                    </Link>
                  </div>
                )}

                <div style={{ display: 'flex', gap: '8px' }}>
                  {(site.status === 'draft' || site.status === 'preview') && (
                    <button
                      onClick={() => setOutreachModalSite(site)}
                      style={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        background: 'rgba(91, 58, 58, 0.15)',
                        color: '#B08080',
                        padding: '8px 16px',
                        borderRadius: '8px',
                        border: '1px solid rgba(91, 58, 58, 0.2)',
                        cursor: 'pointer',
                        fontSize: '13px',
                      }}
                    >
                      <Send style={{ width: '14px', height: '14px' }} />
                      Send to Business
                    </button>
                  )}
                  {site.status === 'sent' && (
                    <>
                      <Link
                        to={`/demo/sites/${site.id}/edit`}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px',
                          background: 'rgba(255,255,255,0.1)',
                          color: '#FFFBFE',
                          padding: '8px 16px',
                          borderRadius: '8px',
                          textDecoration: 'none',
                          fontSize: '13px',
                        }}
                      >
                        <Edit style={{ width: '14px', height: '14px' }} />
                        Edit
                      </Link>
                      <button
                        onClick={() => setOutreachModalSite(site)}
                        style={{
                          flex: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px',
                          background: 'rgba(91, 58, 58, 0.15)',
                          color: '#B08080',
                          padding: '8px 16px',
                          borderRadius: '8px',
                          border: '1px solid rgba(91, 58, 58, 0.2)',
                          cursor: 'pointer',
                          fontSize: '13px',
                        }}
                      >
                        <Send style={{ width: '14px', height: '14px' }} />
                        Send Updated Site
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div
          style={{
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '12px',
            padding: '40px',
            textAlign: 'center',
          }}
        >
          <div style={{
            width: '64px',
            height: '64px',
            borderRadius: '50%',
            background: 'rgba(91, 58, 58, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
          }}>
            <Globe style={{ width: '32px', height: '32px', color: '#B08080' }} />
          </div>
          <h3 style={{ fontSize: '18px', marginBottom: '8px' }}>0 Sites</h3>
          <p style={{ color: '#888888', marginBottom: '16px' }}>
            First scrape leads, then click "Generate Site" on a lead
          </p>
          <Link
            to="/demo/leads"
            style={{
              background: 'linear-gradient(to right, #B08080, #B08080)',
              color: '#FFFBFE',
              padding: '10px 20px',
              borderRadius: '8px',
              textDecoration: 'none',
              display: 'inline-block',
            }}
          >
            View Leads
          </Link>
        </div>
      )}

      {outreachModalSite && createPortal(
        <DemoOutreachModal
          site={outreachModalSite}
          lead={getLead(outreachModalSite.leadId) || null}
          onClose={handleCloseOutreachModal}
        />,
        document.body,
      )}
    </div>
  );
}
